/**
 * 数据时间戳的唯一口径（契约 §18.1、`05-ui-design-system.md` §6.4 规则 7 / §6.8、R-06 ④）。
 *
 * **两个时刻，别混**：
 *   · `serverTime` —— 服务端时钟（快照 `GET /sync/snapshot` 里给的 `serverTime`），用于跨设备对齐；
 *   · `receivedAt` —— **本机接收该数据的时刻**，用于回答「我看到的这份数据有多旧」。
 *
 * 面板上显示的是后者；`serverTime` 只用于「服务端时间与本机差多少」的诊断。
 * **不许**用订单的 `createdAt` 冒充它 —— 那是「顾客什么时候下的单」，与数据新鲜度是两件事：
 * 一张三天前下的单今天被重新拉取，它的 `createdAt` 仍是三天前，而这份数据是刚刚收到的。
 *
 * ⚠️ `GET /orders` 的队列载荷与 `GET /locations/:id/grid` 的响应**都不含**时间戳字段
 * （契约 §18.1 末段：加字段会让 Phase 4/5 冻结的逐字键集合断言变红）。
 * 因此这两处的 `serverTime` 恒为 `null` —— 这是**如实**的读数，不是缺省值凑数：
 * 本机接收时刻本来就只由本机产生，不需要服务端配合。
 */

import { formatBeijingTime } from '@cuito/ui';

/** 数据超过多久算「陈旧」（§6.4 规则 7 冻结的 5 分钟） */
export const DATA_STALE_MS = 5 * 60 * 1000;

export interface DataFreshness {
  /** **本机**接收该数据的时刻（ISO 8601） */
  receivedAt: string;
  /** 服务端给的权威时间（只有 `GET /sync/snapshot` 的 `serverTime` 有；其余数据源为 `null`） */
  serverTime: string | null;
}

export function freshnessOf(receivedAt: Date, serverTime: string | null = null): DataFreshness {
  return { receivedAt: receivedAt.toISOString(), serverTime };
}

/**
 * 是否已超过 `DATA_STALE_MS` 未刷新。
 *
 * 边界取**严格大于**（`>`）：恰好 5 分钟那一刻仍算新鲜 —— 阈值本身是「超过 5 分钟」，
 * 用 `>=` 会让「刚从快照拉回来、正好卡在 5 分钟」的数据被标黄，而判据读的是可见的黄色警示。
 * `null`（还没有任何数据）算陈旧：那时候面板上什么都没有，说它新鲜是撒谎。
 */
export function isStale(f: DataFreshness | null, now: Date): boolean {
  if (f === null) return true;
  return now.getTime() - new Date(f.receivedAt).getTime() > DATA_STALE_MS;
}

/**
 * 面板上的文案：`数据时间 HH:mm:ss`（时区**冻结为 `Asia/Shanghai`**；Phase 9 裁决 A-P9-32）。
 *
 * 这不是装饰性文案，它是 `isStale` 那 5 分钟阈值的**判断输入**：店员靠它决定
 * 「我手上这份数据还能不能信」。所以时区必须是**常量**，不能是**设备设置** ——
 * 终端时区配错时（例如平板被设成 UTC），本机时区会**静默地**把刚收到的数据
 * 显示成 3 小时前（或者反过来，显示成 3 小时之后），而店员**没有任何办法察觉**：
 * 他看到的是一串语法完全正确、数值完全错误的时刻。冻结成东八区之后，
 * 这个判断**与设备设置无关**。
 *
 * **代价，如实写在这里**：若某天真在非中国时区的场地卖货，这里的读数会比店员的
 * 手表差 8 小时。「对照自己的手表」正是上一轮选本机时区的理由，A-P9-32 判它
 * 不足以偿付上面的静默错误。而那一天该改的是**场地时区常量**
 * —— 一处、可评审、可测试、可回归；不是让每个终端的本地设置各自决定判据。
 * 跨设备的绝对对齐另有 `serverTime` 承担（见文件头）。
 *
 * 口径复用 §7.8 的全站唯一实现 `formatBeijingTime`（`packages/ui/src/time.ts`，
 * 那里是 `Asia/Shanghai` 的唯一常量），本文件不再写第二个时区字面量。
 * 它给出的是带日期的完整格式，而 §6.8 冻结的这条文案**只有时间那一段**
 * ⇒ 只取最后一段，形态逐字保持 `HH:mm:ss`。日期部分一律不显示：数据陈旧到跨天时，
 * `isStale` 的黄色警示才是判据，不是这个字符串。
 */
export function formatDataAsOf(f: DataFreshness | null): string {
  if (f === null) return '数据时间 —';
  // 用空白切分而不是定长切片：日期部分的宽度不受 ICU 版本影响，但分隔符属空白类字符，
  // 交给正则比钉死一个下标稳。
  const timePart = formatBeijingTime(f.receivedAt).split(/\s+/).pop() ?? '';
  return `数据时间 ${timePart}`;
}
